import { useState } from "react";
import { Share2, Copy, Check, Facebook, Twitter } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useLang } from "@/lib/lang";
import { toast } from "sonner";

const REDDIT_SVG = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M22 12a2.5 2.5 0 0 0-4.35-1.67c-1.6-1-3.7-1.63-6-1.72l1.02-4.79 3.32.7A2 2 0 1 0 16.5 3l-3.72-.79a.5.5 0 0 0-.6.39l-1.17 5.5c-2.31.08-4.42.72-6.02 1.72A2.5 2.5 0 1 0 2.5 13.4a4.5 4.5 0 0 0-.09.9C2.41 17.6 6.68 20 12 20s9.59-2.4 9.59-5.7c0-.3-.04-.6-.1-.9A2.5 2.5 0 0 0 22 12M7 13a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3m9.65 3.86a4.5 4.5 0 0 1-4.65 1.68 4.5 4.5 0 0 1-4.65-1.68.5.5 0 0 1 .82-.58 3.6 3.6 0 0 0 3.83 1.29 3.6 3.6 0 0 0 3.83-1.29.5.5 0 1 1 .82.58M17 16a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3"/>
  </svg>
);

export default function ShareMenu({ location, testId = "share-menu" }) {
  const { lang } = useLang();
  const [copied, setCopied] = useState(false);

  const name = location.name?.[lang] || location.name?.fr;
  const url = `${window.location.origin}/map?id=${encodeURIComponent(location.id)}`;
  const text = lang === "fr"
    ? `Découvrez le mystère « ${name} » sur WorldMysteriesMap.com`
    : `Discover the mystery "${name}" on WorldMysteriesMap.com`;

  const shareFacebook = () => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, "_blank", "noopener,noreferrer");
  const shareTwitter = () => window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`, "_blank", "noopener,noreferrer");
  const shareReddit = () => window.open(`https://www.reddit.com/submit?url=${encodeURIComponent(url)}&title=${encodeURIComponent(name)}`, "_blank", "noopener,noreferrer");

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success(lang === "fr" ? "Lien copié" : "Link copied");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error(lang === "fr" ? "Erreur" : "Error");
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" data-testid={testId}
                className="border-zinc-300 hover:bg-zinc-50 text-zinc-800 h-8 gap-1.5">
          <Share2 size={13} />
          <span className="text-xs font-mono-data uppercase tracking-wider">
            {lang === "fr" ? "Partager" : "Share"}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-1.5 bg-white border-zinc-200" align="end">
        <button onClick={shareFacebook} data-testid="share-facebook"
                className="w-full flex items-center gap-3 text-left px-3 py-2 rounded-md hover:bg-zinc-50 text-sm text-zinc-800">
          <Facebook size={14} className="text-blue-600" /> Facebook
        </button>
        <button onClick={shareTwitter} data-testid="share-twitter"
                className="w-full flex items-center gap-3 text-left px-3 py-2 rounded-md hover:bg-zinc-50 text-sm text-zinc-800">
          <Twitter size={14} className="text-sky-500" /> X (Twitter)
        </button>
        <button onClick={shareReddit} data-testid="share-reddit"
                className="w-full flex items-center gap-3 text-left px-3 py-2 rounded-md hover:bg-zinc-50 text-sm text-zinc-800">
          <span className="text-orange-500">{REDDIT_SVG}</span> Reddit
        </button>
        <div className="h-px bg-zinc-100 my-1" />
        <button onClick={copyLink} data-testid="share-copy"
                className="w-full flex items-center gap-3 text-left px-3 py-2 rounded-md hover:bg-zinc-50 text-sm text-zinc-800">
          {copied ? <Check size={14} className="text-emerald-600" /> : <Copy size={14} className="text-zinc-500" />}
          {lang === "fr" ? "Copier le lien" : "Copy link"}
        </button>
      </PopoverContent>
    </Popover>
  );
}
